import { Dispatch, SetStateAction } from "react";
import { FacialAuthenticationStatus, FacialLivenessDiagnostic, StatusOperations } from "./results";
import { ResponseLiveness } from "./selphi";

export enum SseStatus {
  connecting = 'connecting',
  open = 'open',
  error = 'error',
  closed = 'closed',
};
export type Assets = {
  type: string;
  url: string;
};

export type SelfieCapturedPayload = {
  asset: Assets;
};

export type Links = {
  document: string;
  asset: Assets[];
};

type AuthenticationImages = {
  type: string;
  url: string;
  links?: {
    document: string;
  }
};

type PassiveLivenessEvaluated = {
  diagnostic: FacialLivenessDiagnostic;
}

export type FacialAuthenticationPayload = {
  similarity: number;
  authStatus: FacialAuthenticationStatus;
  links?: {
    document: string;
    assets: AuthenticationImages[],
  }
};

export type IdValitatedPayload = 
  {
    succeed: boolean;
    data: {
      scanReference: string;
      code: number;
      person: {
        gender: string | null;
        idNumber: string;
        lastName: string;
        firstName: string;
        dateOfBirth: string;
      };
      reason: string | null;
      status: string;
      document: {
        type: string;
        number: string;
        country: string;
        validUntil: string;
      };
      reasonCode: string | null;
      merchantScanReference: string | null;
      decisionTime: string;
      acceptanceTime: string;
    };
    summary: Record<string, unknown>;
    details: {
      country: string;
      dateOfBirth: string;
      firstName: string;
      idNumber: string;
      lastName: string;
      number: string;
      type: string;
      validUntil: string;
    };
    source: string;
    mode: string;
    links: {
      document: string;
      assets: Assets[];
    };
  };

export type FacialExtractionPayload = {
  age: number;
  faceConfidence: number;
  gender: string;
  glasses: string;
  iod: number;
};

export type OperationFinishedPayload = {
  status: StatusOperations;
};

export type OperationContextPayload = {
  context: {
    customerId: string;
  }
};

export type CivilRegistryDetails = {
  firstName: string;
  lastName: string;
  issuer: string;
  documentNumber: string;
  dateOfBirth: string;
  placeOfBirth: string;
  address: string;
  dateOfExpiry: string;
};

export type CivilRegistryValidatedPayload = {
  succeed: boolean;
  data: Record<string, any>;
  serviceDocument: IdCapturedPayload;
  result?: string,
  summary?: {
    facialMatching: boolean;
    facialMatchingStatus: FacialAuthenticationStatus;
    links: Links;
  };
  details: Record<string, string>;
  source: string;
  mode: string;
};

export type IdCapturedPayload = {
  BACKSIDE: {
    FIELD_DATA: {
      ADDRESS: string[];
      BIRTH_PLACE: string[];
      SON_OF: string;
      TEAM: string;
    };
    MRZ_DATA: {
      BIRTH_DATE: string;
      EXPIRATION_DATE: string;
      IDENTITY_NUMBER: string;
      ISSUING_COUNTRY: string;
      NAME: string;
      NATIONALITY: string;
      PERSONAL_NUMBER: string;
      SEX: string;
      SURNAME: string;
    };
  };
  CHECKS: {
    BIRTH_DATE_SIDE_MATCH: boolean;
    EXPIRATION_DATE_SIDE_MATCH: boolean;
    IDENTITY_NUMBER_SIDE_MATCH: boolean;
    NAME_SIDE_MATCH: boolean;
    PERSONAL_NUMBER_SIDE_MATCH: boolean;
    SEX_SIDE_MATCH: boolean;
    SURNAME_SIDE_MATCH: boolean;
  };
  DECOMPOSED: {
    FACE: string;
  };
  DOC_MODEL: string;
  FRONTSIDE: {
    FIELD_DATA: {
      ADDRESS: string;
      BIRTH_DATE: string;
      BIRTH_PLACE: string;
      EXPIRATION_DATE: string;
      IDENTITY_NUMBER: string;
      DOCUMENT_NUMBER: string;
      NAME: string;
      PERSONAL_NUMBER: string;
      SEX: string;
      SURNAME: string[];
      SURNAMES: string[];
      NATIONALITY: string;
    };
    MRZ_DATA: {
      BIRTH_DATE: string;
      EXPIRATION_DATE: string;
      IDENTITY_NUMBER: string;
      ISSUING_COUNTRY: string;
      NAME: string;
      NATIONALITY: string;
      PERSONAL_NUMBER: string;
      SEX: string;
      SURNAME: string;
    };
  };
  SCORING: {
    FIELDS_RETURNED: number;
    FIELDS_TOTAL: number;
    OVERALL_RATING: number;
    FRONT_CONFIDENCE: number;
    BACK_CONFIDENCE: number;
    OVERALL_SIDE_CORRESPONDENCE: number;
  };
};

export type PersonalInformationPayload = {
  name: string;
  surname: string;
  nationality: string;
  documentNumber: string;
  birthDate: string;
  birthPlace: string;
  address: string;
  expirationDate: string;
};

export enum SseAssetsTypes {
  SELFIE = 'selfie',
  ID_PORTRAIT = 'id_portrait',
};

export type PreviousOnboardingPayload = {
  found: boolean;
  operationId: string;
  assets: Assets[];
};

export enum SseEvents {
  ID_CAPTURED = 'ID_CAPTURED',
  SELFIE_CAPTURED = 'SELFIE_CAPTURED',
  FACIAL_AUTHENTICATION_EVALUATED = 'FACIAL_AUTHENTICATION_EVALUATED',
  PASSIVE_LIVENESS_EVALUATED = 'PASSIVE_LIVENESS_EVALUATED',
  ID_VALIDATED = 'ID_VALIDATED',
  CIVIL_REGISTRY_VALIDATED = 'CIVIL_REGISTRY_VALIDATED',
  PREVIOUS_ONBOARDING_VALIDATED = 'PREVIOUS_ONBOARDING_VALIDATED',
  OPERATION_FINISHED = 'OPERATION_FINISHED',
  OPERATION_STARTED = 'OPERATION_STARTED',
  FACIAL_EXTRACTION_EVALUATED = 'FACIAL_EXTRACTION_EVALUATED',
  OPERATION_EXPIRED = 'OPERATION_EXPIRED',
  PERSONAL_INFORMATION_GATHERED = 'PERSONAL_INFORMATION_GATHERED',
  HEARTBEAT_EVENT = 'Heartbeat',
};

export type SseResultObject = {
  [SseEvents.OPERATION_STARTED]?: OperationContextPayload;
  [SseEvents.ID_CAPTURED]?: IdCapturedPayload;
  [SseEvents.SELFIE_CAPTURED]?: SelfieCapturedPayload;
  [SseEvents.FACIAL_AUTHENTICATION_EVALUATED]?: FacialAuthenticationPayload;
  [SseEvents.FACIAL_EXTRACTION_EVALUATED]?: FacialExtractionPayload;
  [SseEvents.PASSIVE_LIVENESS_EVALUATED]?: ResponseLiveness;
  [SseEvents.ID_VALIDATED]?: IdValitatedPayload;
  [SseEvents.CIVIL_REGISTRY_VALIDATED]?: CivilRegistryValidatedPayload;
  [SseEvents.PREVIOUS_ONBOARDING_VALIDATED]?: PreviousOnboardingPayload;
  [SseEvents.OPERATION_FINISHED]?: OperationFinishedPayload;
  [SseEvents.OPERATION_EXPIRED]?: OperationContextPayload;
  [SseEvents.PERSONAL_INFORMATION_GATHERED]?: PersonalInformationPayload;
};

export type SseData = {
  url: string;
  setStatus: Dispatch<SetStateAction<SseStatus>>;
  toastError: () => void;
  dispatchResults: (event: MessageEvent) => void;
  setIsOperationFinished: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<boolean>>;
  handleTimer: (event: MessageEvent) => void;
};

export type ReloadResultsResponseDto = {
  operation_finished: OperationFinishedPayload;
  passive_liveness_evaluated: PassiveLivenessEvaluated;
  facial_authentication_evaluated: FacialAuthenticationPayload;
  personal_information_gathered: PersonalInformationPayload;
  previous_onboarding_validated: PreviousOnboardingPayload;
  civil_registry_validated: CivilRegistryValidatedPayload;
  id_validated: IdValitatedPayload;
  id_captured: IdCapturedPayload;
  selfie_captured: SelfieCapturedPayload;
  facial_extraction_evaluated: FacialExtractionPayload;
} | undefined;